import React from "react";
import PageHero from "@/components/sections/PageHero";
import CTABanner from "@/components/sections/CTABanner";
import SEO, { localBusinessSchema } from "@/components/SEO";
import { Link } from "react-router-dom";

const VALUES = [
    { title: "Performance First", body: "We believe music is meant to be shared. Regular stage time turns students into confident performers." },
    { title: "Disciplined Curriculum", body: "Certified international pathways (ABRSM, Trinity, Rockschool) combined with creative repertoire." },
    { title: "Creative Exploration", body: "Ensembles, songwriting, production and improvisation — we nurture every side of musicianship." },
    { title: "Community", body: "Open-mic evenings, student concerts and masterclasses — musicians grow best around other musicians." },
];

const ACTIVITIES = [
    { title: "Termly Recitals", body: "Solo and group recitals held every term on our performance stage." },
    { title: "Live Concerts", body: "Annual showcase concert — students perform with a full live band." },
    { title: "Jam Nights", body: "Monthly open jams to practise improvisation and ensemble playing." },
    { title: "Masterclasses", body: "Guest sessions from touring musicians and conservatory faculty." },
];

export default function About() {
    return (
        <div data-testid="page-about">
            <SEO
                title="About Ihao Music House Delhi | Premium Music Academy in Malviya Nagar"
                description="Founded in 2022, Ihao Music House Delhi is a premium Western music and performing arts academy in Malviya Nagar, South Delhi — opposite Select Citywalk, Saket."
                path="/about"
                schema={localBusinessSchema()}
            />
            <PageHero
                eyebrow="About Ihao"
                title="A music academy built around performance, discipline and joy."
                subtitle="Founded in 2022 in Malviya Nagar, Delhi — we set out to build a studio where every student, from first-time beginner to grade-certified musician, feels inspired to practise and perform."
            />

            {/* Story */}
            <section className="relative py-20 md:py-28 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
                    <div className="relative">
                        <img src="https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=1100&q=80" alt="Studio" className="rounded-3xl border border-white/10 w-full" />
                        <div className="absolute -bottom-6 -right-6 hidden md:flex flex-col gap-1 p-6 rounded-2xl bg-gold text-black max-w-[220px]">
                            <span className="font-display text-4xl font-bold">500+</span>
                            <span className="text-xs font-semibold uppercase tracking-wider">Students trained</span>
                        </div>
                    </div>

                    <div>
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">Our story</span>
                        <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold text-white leading-tight">
                            Founded in 2022 with one simple idea: make serious music education approachable.
                        </h2>
                        <p className="mt-6 text-white/65 leading-relaxed">
                            We saw a gap in South Delhi — either you joined a large commercial chain or struggled to find an individual teacher. Ihao Music House was built to be the middle ground: a boutique academy with international standards, personal attention and the facilities of a recording studio.
                        </p>
                        <p className="mt-4 text-white/65 leading-relaxed">
                            Today, our studio near DLF Avenue Saket trains students across piano, guitar, singing, drums, violin, bass, ukulele, music production, live sound and DJing — guided by instructors with conservatory training and years of live performance.
                        </p>
                    </div>
                </div>
            </section>

            {/* Mission / Vision */}
            <section className="relative py-20 md:py-28 bg-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="rounded-3xl border border-gold/30 p-10 bg-gradient-to-br from-[#1a1405] to-[#0a0a0a]">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Mission</span>
                        <h3 className="mt-3 font-display text-2xl md:text-3xl text-white font-semibold leading-tight">Train the next generation of confident, versatile musicians.</h3>
                        <p className="mt-4 text-white/65 leading-relaxed">We commit to honest feedback, disciplined practice frameworks and real stage time — every student, every month.</p>
                    </div>
                    <div className="rounded-3xl border border-white/10 p-10 bg-[#121212]">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Vision</span>
                        <h3 className="mt-3 font-display text-2xl md:text-3xl text-white font-semibold leading-tight">South Delhi's most loved music academy — for life-long learners.</h3>
                        <p className="mt-4 text-white/65 leading-relaxed">A creative ecosystem where students graduate not just with certificates, but with communities, songs and memories.</p>
                    </div>
                </div>
            </section>

            {/* Values */}
            <section className="relative py-20 md:py-28 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-14">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">Philosophy</span>
                        <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold text-white leading-tight">What we stand for.</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {VALUES.map((v, i) => (
                            <div key={v.title} className="rounded-2xl border border-white/10 bg-[#121212] p-8 hover:border-gold/30 transition-colors">
                                <span className="text-gold font-display text-4xl font-light">{String(i + 1).padStart(2, "0")}</span>
                                <h3 className="mt-4 text-white font-display text-xl font-semibold">{v.title}</h3>
                                <p className="mt-3 text-white/60 leading-relaxed text-sm">{v.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Activities */}
            <section className="relative py-20 md:py-28 bg-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                        <div>
                            <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">Studio Life</span>
                            <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold text-white leading-tight max-w-xl">Concerts, recitals and creative collaborations.</h2>
                        </div>
                        <p className="text-white/55 max-w-md">Our calendar is built around performance — because that's how musicians grow.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {ACTIVITIES.map((a, i) => (
                            <div key={a.title} className="rounded-2xl border border-white/10 bg-[#121212] p-7 hover:-translate-y-1 hover:border-gold/40 transition-all duration-300">
                                <span className="inline-block text-gold text-5xl font-display font-light leading-none">{String(i + 1).padStart(2, "0")}</span>
                                <h3 className="mt-5 font-display text-xl text-white font-semibold">{a.title}</h3>
                                <p className="mt-3 text-sm text-white/55 leading-relaxed">{a.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Faculty */}
            <section className="relative py-20 md:py-28 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-14">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">Faculty</span>
                        <h2 className="mt-3 font-display text-3xl md:text-5xl font-semibold text-white leading-tight">Teachers who are performers first.</h2>
                        <p className="mt-4 text-white/60 max-w-2xl mx-auto">Our instructors are active performers, studio musicians and certified educators. They bring the stage into the classroom.</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { role: "Piano Lead", exp: "ABRSM certified · 9+ years" },
                            { role: "Guitar Lead", exp: "Rockschool · Session guitarist" },
                            { role: "Vocals Lead", exp: "Trinity Grade 8 · Playback artist" },
                            { role: "Production Lead", exp: "Logic Pro & Ableton certified" },
                        ].map((f, i) => (
                            <div key={i} className="rounded-2xl border border-white/10 bg-[#121212] p-7 text-center hover:border-gold/40 transition-colors">
                                <div className="mx-auto w-20 h-20 rounded-full border border-gold/40 flex items-center justify-center mb-5">
                                    <span className="font-display text-gold text-2xl">{f.role.split(" ")[0][0]}</span>
                                </div>
                                <h4 className="font-display text-white text-lg font-semibold">{f.role}</h4>
                                <p className="mt-2 text-xs text-white/50 uppercase tracking-wider">{f.exp}</p>
                            </div>
                        ))}
                    </div>
                    <p className="mt-10 text-center text-sm text-white/50">
                        Want to meet your teacher? <Link to="/contact" className="text-gold hover:text-gold-hover border-b border-gold/40">Book a free demo</Link> and we'll match you with the right mentor.
                    </p>
                </div>
            </section>

            <CTABanner title="Ready to start your musical journey?" subtitle="A free demo class is the best way to see if Ihao is right for you. Meet the instructor, see the studio, play an instrument." />
        </div>
    );
}
